import { isInputValueIncluded } from "components/Form/components/Autocomplete/Autocomplete.service";
import { AsyncSelect } from "components/Form/components/Autocomplete/components/AsyncSelect";
import { DropdownContainer } from "components/Form/components/Autocomplete/components/DropdownContainer";
import { SearchableSelect } from "components/Form/components/Autocomplete/components/SearchableSelect";
import { SimpleSelect } from "components/Form/components/Autocomplete/components/SimpleSelect";
import { useAsyncSelect } from "components/Form/components/Autocomplete/useAsyncSelect";
import { useAutocompleteMenuPortal } from "components/Form/components/Autocomplete/AutocompleteMenuPortalContext";
import { RecordType } from "graphql/generated/graphql";
import { MdiReactIconComponentType } from "mdi-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select/dist/declarations/src/Select";
import { getListFromGraphql, SingleField, useOutsideAutocompleteClick } from "utils/utils";
import "./Autocomplete.scss";
import {
    AutocompleteSearchMenuList,
    ClearIndicator,
    DropdownIndicator,
    GroupLabel,
    IconOptionGenerator,
    LoadingMessage,
    NoOptionsMessage,
    OptionLabel,
    SingleValueWithIconReplacement,
    ValueContainer,
} from "./ReactSelectComponents";

export interface AutocompleteOption {
    value: any;
    label: string;
    icon?: any;
    iconName?: string;
    is_readonly?: boolean;
    type?: Lowercase<RecordType>;
}

export interface GroupOptions {
    label: string;
    options: AutocompleteOption[];
}

export interface GroupAutocompleteOptions {
    groupOptions: GroupOptions[];
    flatList: AutocompleteOption[];
}

export interface AutocompleteOptionsProps {
    resource?: string; // TODO remove this, this Autocomplete should not know anything about resources
    enumName?: string; // TODO remove this, this Autocomplete should not know anything about resources
    list?: AutocompleteOption[];
    hasNameAsOptionValue?: boolean;
    queryField?: SingleField;
    group?: GroupAutocompleteOptions;
    iconReplacement?: MdiReactIconComponentType;
    filterOptions?: (option: AutocompleteOption) => boolean;
    resourceBody?: string;
    isCreatable?: boolean;
    applyActionOnCreate?: () => void;
    applyActionOnChange?: () => void;
    hasInitialValuesLoaded?: boolean;
}

export const getSelectedValue = ({
    defaultValue,
    value,
    options,
    isDropdown,
}: {
    defaultValue?: AutocompleteOption;
    value: any;
    options?: AutocompleteOption[];
    isDropdown?: boolean;
}) => {
    if (defaultValue !== undefined && !value) {
        return defaultValue;
    }

    // if there is an input value provided, try to map the id it to the corresponding label
    // if this fails, just display the value itself
    if (value != null && value !== "") {
        // if the option is -1 and is a dropdown, "Invalid choice will be displayed"
        if (value === -1 && isDropdown) {
            return { value: -1, label: "Invalid choice" };
        }

        // if a list of options is provided, use it instead of the state
        const foundOption = (options ?? []).find((item) => item.value === value);

        // otherwise it returns the found option or fallback option
        return foundOption ?? { value, label: value };
    }

    // otherwise, there is no selection to be displayed
    return null;
};

// this component only supports single selections
// if multiselect is needed, it may be helpful to view single select as
// a special case of multiselect, where there is only 1 select item allowed
// in other words, the multiselect component should be able to handle single select as well
// to achieve this, one could start by changing the single select to return an array
// with only one result in it.
export const Autocomplete = ({
    name,
    options = {},
    defaultValue,
    value,
    onChange,
    onBlur = () => {},
    additionalProps = {},
    isDisabled = false,
}: {
    name: string;
    options: AutocompleteOptionsProps;
    defaultValue?: AutocompleteOption;
    value?: any;
    onChange: (selectedValue: any) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    additionalProps?: any;
    isDisabled?: boolean;
}) => {
    const [isOpened, setOpened] = useState(false);
    const menuRef = useRef<Select>();

    // Inside a record-prompt modal, portal the option menu to document.body so it
    // escapes the modal's `overflow-y: auto` clip (see AutocompleteMenuPortalContext).
    // An explicit `additionalProps.menuInModal` still forces it on if ever needed.
    const menuPortalFromContext = useAutocompleteMenuPortal();
    const menuInModal = additionalProps?.menuInModal || menuPortalFromContext;

    const { list, group, resource: resourceName, enumName } = options;

    const { applyActionOnCreate, applyActionOnChange, isCreatable } = additionalProps ?? {};

    const optionList = useMemo(() => list ?? group?.flatList, [group?.flatList, list]);

    const enumOptionList = useMemo(() => {
        if (enumName) {
            const optionList = getListFromGraphql(enumName) || [];
            const mappedOptions = [...optionList].map((item) => ({
                value: item,
                label: item,
                link: null,
                icon: null,
            }));

            const sortedOptions = mappedOptions.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });

            return sortedOptions;
        }
    }, [enumName]);

    const staticOptions = useMemo(() => {
        if (optionList === undefined && enumOptionList === undefined) {
            return;
        }
        const copyOptions = [...(optionList ?? enumOptionList ?? [])];
        if (
            !group?.flatList &&
            defaultValue &&
            !copyOptions.find((option) => option.value === defaultValue.value)
        ) {
            copyOptions.unshift(defaultValue);
        }
        return copyOptions;
    }, [optionList, enumOptionList, group?.flatList, defaultValue]);
    const iri = useMemo(
        () => `${name || additionalProps?.customKey}-${resourceName}`,
        [additionalProps?.customKey, name, resourceName]
    );
    const {
        isLoading,
        term,
        setTerm,
        hasMore,
        loadOptions,
        handleMenuOpen,
        currentPageRef,
        dynamicOptions,
        hasLoadedOptions,
        dynamicSelectedOption,
    } = useAsyncSelect(
        options,
        value,
        iri,
        defaultValue,
        additionalProps?.hasInitialValuesLoaded,
        additionalProps?.initialAutocompleteOption
    );

    const [currentScrollPosition, setCurrentScrollPosition] = useState(0);

    const toggleOpened = useCallback(() => {
        const newOpenState = !isOpened;
        setOpened(newOpenState);
        setCurrentScrollPosition(0);
        if (newOpenState && resourceName) {
            handleMenuOpen();
        }
    }, [handleMenuOpen, isOpened, resourceName]);
    const noIcons =
        staticOptions !== undefined
            ? staticOptions.some((item) => item.icon)
            : dynamicOptions?.some((item) => item.icon) ?? false;

    // generate the option component
    // passing the default value is necessary to properly highlight it
    // noIcons mode is used to fix weird indentation when no icons are present
    const IconOption = useMemo(
        () => IconOptionGenerator(defaultValue, noIcons, options?.iconReplacement),
        [defaultValue, noIcons, options?.iconReplacement]
    );

    const selectedValue = useMemo(() => {
        return staticOptions === undefined
            ? dynamicSelectedOption
            : getSelectedValue({
                  value,
                  defaultValue,
                  isDropdown: additionalProps.isDropdown,
                  options: staticOptions,
              });
    }, [dynamicSelectedOption, value, defaultValue, additionalProps.isDropdown, staticOptions]);

    const onChangeValue = useCallback(
        (selectedOption) => {
            if (selectedOption) {
                onChange(selectedOption.value);
            } else {
                onChange(null);
            }
            applyActionOnChange?.();
            isOpened && toggleOpened();
        },
        [applyActionOnChange, isOpened, onChange, toggleOpened]
    );

    const isSearchInputHidden = useMemo(
        () => staticOptions && staticOptions.length < 5 && !isCreatable,
        [isCreatable, staticOptions]
    );

    // remap config objects for react-select
    const configMapper = useCallback(
        (config) => {
            // shallow copy is okay since we only transform top level properties
            const newConfig = { ...config };
            if ("clearable" in newConfig) {
                newConfig.isClearable = newConfig.clearable;
                delete newConfig.clearable;
            }
            if ("disabled" in newConfig) {
                newConfig.isDisabled = newConfig.disabled;
                delete newConfig.disabled;
            }
            if (!isSearchInputHidden && "placeholder" in newConfig) {
                delete newConfig.placeholder;
            }
            if ("customKey" in newConfig) {
                delete newConfig.customKey;
            }
            if ("fieldWidth" in newConfig) {
                delete newConfig.fieldWidth;
            }
            if ("maxFieldWidth" in newConfig) {
                delete newConfig.maxFieldWidth;
            }
            if ("menuInModal" in newConfig) {
                delete newConfig.menuInModal;
            }

            return newConfig;
        },
        [isSearchInputHidden]
    );

    useEffect(() => {
        if (isOpened && menuRef.current) {
            menuRef.current.focus();
        }
    }, [isOpened]);

    const uniqueKey = `${additionalProps?.recordId ? `${additionalProps?.recordId}-` : ""}${
        additionalProps?.customKey ?? resourceName ?? enumName ?? ""
    }-${name}`;

    useOutsideAutocompleteClick(menuRef, uniqueKey, () => {
        if (isOpened) {
            setOpened(false);
        }
    });

    const formatGroupLabel = useMemo(
        () => (group ? (data) => <GroupLabel label={data.label} /> : null),
        [group]
    );

    const sharedProps = useMemo(
        () => ({
            uniqueKey,
            menuPortalTarget: menuInModal ? document.body : null,
            menuPosition: menuInModal ? "fixed" : undefined,
            unstyled: true,
            onBlur,
            onChange: onChangeValue,
            iconReplacement: options?.iconReplacement,
            components: {
                ClearIndicator,
                DropdownIndicator,
                SingleValue: SingleValueWithIconReplacement,
                NoOptionsMessage,
                LoadingMessage,
                Option: IconOption,
            },
            isClearable: additionalProps?.clearable !== false,
            id: name,
            name: name,
            placeholder: "",
            classNamePrefix: "react-select",
            styles: {
                container: () => ({
                    width: additionalProps?.fieldWidth ?? "100%",
                    maxWidth: additionalProps?.maxFieldWidth,
                    position: "relative",
                }),
                menu: () => ({
                    marginTop: "5px",
                }),
                // In-modal: lift the portaled menu above the modal layer (root
                // z-index 2000) so it is not painted behind the dialog.
                ...(menuInModal
                    ? { menuPortal: (base) => ({ ...base, zIndex: 2200 }) }
                    : {}),
            },
            getOptionLabel: (option: AutocompleteOption) => (
                <OptionLabel
                    option={option}
                    showReadOnlyChip={additionalProps?.showReadOnlyChip}
                    defaultValue={defaultValue}
                    iconReplacement={options?.iconReplacement}
                    removeOption={
                        additionalProps?.iconOnlyComponent &&
                        additionalProps?.clearable !== false &&
                        selectedValue?.value === option?.value
                            ? (e) => {
                                  e?.stopPropagation();
                                  e?.preventDefault();
                                  currentPageRef.current = -1;
                                  onChangeValue(null);
                              }
                            : undefined
                    }
                />
            ),
            formatGroupLabel,
            ...configMapper(additionalProps),
            isDisabled,
        }),
        [
            IconOption,
            additionalProps,
            configMapper,
            currentPageRef,
            defaultValue,
            formatGroupLabel,
            isDisabled,
            menuInModal,
            name,
            onBlur,
            onChangeValue,
            options?.iconReplacement,
            selectedValue?.value,
            uniqueKey,
        ]
    );

    const hasSelectedValue = useMemo(() => {
        const hasValue = (value) => value != null && value !== "";
        return (
            selectedValue != null &&
            (hasValue(selectedValue?.label) || hasValue(selectedValue?.value))
        );
    }, [selectedValue]);

    const isSelectedOptionHidden = useMemo(
        () =>
            term?.trim() && hasSelectedValue
                ? !isInputValueIncluded(selectedValue as AutocompleteOption, term)
                : false,
        [hasSelectedValue, selectedValue, term]
    );

    const autocompleteMenuListExtras = useMemo(
        () => ({
            hasMoreData: resourceName ? hasMore : false,
            hasInfiniteLoader: !!resourceName,
            isDataLoading: isLoading,
            loadMore: async () => {
                if (hasLoadedOptions) {
                    await loadOptions(term);
                }
            },
            currentScrollPosition,
            setCurrentScrollPosition,
            isSelectedOptionHidden,
            selectedValue,
        }),
        [
            resourceName,
            hasMore,
            isLoading,
            hasLoadedOptions,
            loadOptions,
            term,
            currentScrollPosition,
            isSelectedOptionHidden,
            selectedValue,
        ]
    );

    const SearchSelectClearIndicator = useMemo(() => () => null, []);

    const selectComponents = useMemo(
        () => ({
            ValueContainer,
            ClearIndicator: SearchSelectClearIndicator,
            MenuList: AutocompleteSearchMenuList,
        }),
        [SearchSelectClearIndicator]
    );

    const selectWithSearchProps = useMemo(
        () => ({
            menuIsOpen: true,
            controlShouldRenderValue: false,
            autocompleteMenuListExtras,
            components: selectComponents,
            placeholder: isCreatable ? "Type to search or create new option" : "Search...",
            styles: {
                container: () => ({
                    display: isOpened ? "block" : "none",
                    width: additionalProps?.fieldWidth ?? "100%",
                    maxWidth: additionalProps?.maxFieldWidth,
                    position: "relative",
                }),
            },
            isClearable: false,
            isSearchable: true,
            isValidNewOption: isCreatable ? undefined : () => false,
            onCreateOption: (inputValue) => {
                onChange(inputValue);
                applyActionOnCreate?.();
                isOpened && toggleOpened();
            },
            filterOption: null,
        }),
        [
            selectComponents,
            autocompleteMenuListExtras,
            isCreatable,
            isOpened,
            additionalProps?.fieldWidth,
            additionalProps?.maxFieldWidth,
            onChange,
            applyActionOnCreate,
            toggleOpened,
        ]
    );

    const dropdownProps = {
        uniqueKey,
        name,
        selectedValue,
        isOpened,
        toggleOpened,
        additionalProps,
        iconReplacement: options?.iconReplacement,
        clearValue: (e) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(null);
            applyActionOnChange?.();
        },
    };

    const creatableSelectProps = {
        ...sharedProps,
        ...selectWithSearchProps,
    };

    if (staticOptions !== undefined) {
        const groupOptions = group?.groupOptions !== undefined ? group.groupOptions : undefined;
        if (staticOptions.length < 5 && !isCreatable) {
            return (
                <SimpleSelect
                    optionList={staticOptions}
                    selectedValue={selectedValue}
                    groupOptions={groupOptions}
                    {...sharedProps}
                />
            );
        }

        return (
            <DropdownContainer {...dropdownProps}>
                <SearchableSelect
                    optionList={staticOptions}
                    groupOptions={groupOptions}
                    selectedValue={selectedValue}
                    menuRef={menuRef}
                    defaultValue={defaultValue}
                    {...creatableSelectProps}
                />
            </DropdownContainer>
        );
    } else {
        return (
            <DropdownContainer {...dropdownProps}>
                <AsyncSelect
                    key={`${uniqueKey}-${isOpened}`}
                    menuRef={menuRef}
                    selectedValue={selectedValue}
                    loadOptions={loadOptions}
                    handleMenuOpen={handleMenuOpen}
                    currentPageRef={currentPageRef}
                    setTerm={setTerm}
                    dynamicOptions={dynamicOptions}
                    isSelectedOptionHidden={isSelectedOptionHidden}
                    {...creatableSelectProps}
                />
            </DropdownContainer>
        );
    }
};
